<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';

const props = withDefaults(defineProps<{
  storageKey: string;
  detailOpen: boolean;
  defaultSplit?: number;
  defaultSplitWide?: number;
  wideBreakpoint?: number;
  initialOpenPercent?: number;
  minPercent?: number;
  maxPercent?: number;
  presets?: number[];
}>(), {
  defaultSplit: 30,
  defaultSplitWide: 50,
  wideBreakpoint: 1536,
  initialOpenPercent: 30,
  minPercent: 20,
  maxPercent: 80,
  presets: () => [70, 50, 30],
});

const emit = defineEmits<{
  'update:splitPercent': [value: number];
}>();

const splitContainerRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const showGutterHint = ref(!localStorage.getItem(props.storageKey));

const splitPercent = ref(loadSplitPercent());

function getDefaultSplit(): number {
  return window.innerWidth >= props.wideBreakpoint ? props.defaultSplitWide : props.defaultSplit;
}

function loadSplitPercent(): number {
  const stored = localStorage.getItem(props.storageKey);
  if (stored) {
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : getDefaultSplit();
  }
  return getDefaultSplit();
}

function saveSplitPercent(value: number) {
  splitPercent.value = value;
  localStorage.setItem(props.storageKey, String(Math.round(value)));
  emit('update:splitPercent', value);
}

function onGutterMouseDown(e: MouseEvent) {
  e.preventDefault();
  isDragging.value = true;
  showGutterHint.value = false;
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
}

function onDragMove(e: MouseEvent) {
  if (!splitContainerRef.value) return;
  const rect = splitContainerRef.value.getBoundingClientRect();
  splitPercent.value = Math.min(
    props.maxPercent,
    Math.max(props.minPercent, ((e.clientX - rect.left) / rect.width) * 100),
  );
}

function onDragEnd() {
  isDragging.value = false;
  saveSplitPercent(splitPercent.value);
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
}

function onKeyDown(e: KeyboardEvent) {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const tag = (e.target as HTMLElement)?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
  if (!props.detailOpen) return;

  if (e.key === 'q' || e.key === 'e') {
    e.preventDefault();
    cycleSplit(e.key === 'q' ? 1 : -1);
  }
}

function cycleSplit(direction: -1 | 1) {
  const closest = props.presets.reduce((prev, curr) =>
    Math.abs(curr - splitPercent.value) < Math.abs(prev - splitPercent.value) ? curr : prev,
  );
  const idx = props.presets.indexOf(closest);
  const next = Math.min(props.presets.length - 1, Math.max(0, idx + direction));
  saveSplitPercent(props.presets[next]);
}

function setInitialOpenPercent() {
  saveSplitPercent(props.initialOpenPercent);
}

const listStyle = computed(() =>
  props.detailOpen ? { width: splitPercent.value + '%' } : undefined,
);

onMounted(() => {
  document.addEventListener('keydown', onKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
});

defineExpose({ splitPercent, setInitialOpenPercent });
</script>

<template>
  <div
    ref="splitContainerRef"
    class="flex flex-1"
    :class="{ 'select-none': isDragging }"
    data-testid="split-pane-container"
  >
    <div :style="listStyle" :class="{ 'w-full': !detailOpen }">
      <slot name="list" />
    </div>

    <div
      v-if="detailOpen"
      data-testid="split-pane-gutter"
      class="w-1.5 shrink-0 cursor-col-resize relative group transition-colors bg-surface-200 dark:bg-surface-700 hover:bg-primary/40 active:bg-primary/60"
      :class="{ 'bg-primary/60': isDragging }"
      v-tooltip.right="{ value: 'Drag to resize\nQ / E to adjust', class: 'text-xs whitespace-pre' }"
      @mousedown="onGutterMouseDown"
    >
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <span class="w-1 h-1 rounded-full bg-surface-500" />
        <span class="w-1 h-1 rounded-full bg-surface-500" />
        <span class="w-1 h-1 rounded-full bg-surface-500" />
      </div>
      <div
        v-if="showGutterHint"
        class="absolute top-4 left-1/2 -translate-x-1/2 bg-surface-800 text-surface-0 text-xs px-2 py-1 rounded whitespace-nowrap z-10 pointer-events-none animate-pulse"
      >
        Drag to resize
      </div>
    </div>

    <div v-if="detailOpen" class="flex-1 overflow-hidden min-w-0">
      <slot name="detail" />
    </div>
  </div>
</template>
